// Web port of the iOS SoundService submit beats — the question-submission BUMP
// and the streak-celebration FANFARE (the "seal") — synthesized with the Web
// Audio API so the web play flow gets the same audio identity as the app.
//
// Faithful to the iOS procedural synthesis (ios/.../Services/SoundService.swift
// playSubmitSeal()/makeSubmitSealBuffer()): a rising flare (G3→C5 glide) landing
// on a staggered C-major arpeggio (C4 root + C5-E5-G5-C6) with a C7 shimmer on
// top — ONE gesture, not a rotating melody (Tom 2026-07-16: "a coin-drop or a
// payment ding" is the target, not a tune). Bonus days (streak % 6 === 0) get
// the SAME seal with the arpeggio extended one octave up (E6-G6-C7) after the
// base chord lands, ringing out longer — "the usual sound, but it kept going,"
// not a different song. The two play moments mirror iOS:
//   • playSubmitBump()    — fires on the submit-button TAP (110 Hz + 220 Hz,
//                           ~240 ms) so the gesture feels grounded.
//   • playSubmitFanfare() — fires when the celebration's streak number lands
//                           (the seal, or its bonus-day extension).
//
// Gating mirrors iOS shouldSkipForAccessibility(): we skip when the user has
// muted sound (localStorage) OR the OS reports prefers-reduced-motion. Browsers
// require a user gesture before audio can start, so unlockAudio() must be called
// from the submit click handler (a real gesture) before the first sound.

let ctx = null;
let master = null;

const MUTE_KEY = 'ps_sound_muted';

function prefersReducedMotion() {
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true;
  } catch {
    return false;
  }
}

export function isSoundMuted() {
  try {
    return localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setSoundMuted(muted) {
  try {
    if (muted) localStorage.setItem(MUTE_KEY, '1');
    else localStorage.removeItem(MUTE_KEY);
  } catch {
    /* storage unavailable — non-fatal */
  }
}

function shouldSkip() {
  return isSoundMuted() || prefersReducedMotion();
}

// Lazily create + resume the AudioContext. MUST be called from within a user
// gesture (the submit click) the first time, or the context starts suspended and
// the browser refuses to play. Safe to call repeatedly.
export function unlockAudio() {
  if (shouldSkip()) return;
  try {
    if (!ctx) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      ctx = new AC();
      master = ctx.createGain();
      master.gain.value = 0.9; // iOS applies a per-sound masterVolume of ~0.50 on top
      master.connect(ctx.destination);
    }
    if (ctx.state === 'suspended') ctx.resume();
  } catch {
    /* audio unavailable — stay silent, never throw into the submit flow */
  }
}

// Equal-tempered pitches used by the fanfares (Hz), matching SoundService.swift.
const HZ = {
  C4: 261.63,
  G4: 392.0,
  C5: 523.25,
  E5: 659.25,
  G5: 783.99,
  A5: 880.0,
  B5: 987.77,
  C6: 1046.5,
  E6: 1318.51,
  G6: 1567.98,
  C7: 2093.0,
};

// Build a PeriodicWave from sine-partial amplitudes (the imag coefficients).
// Reproduces an iOS additive voice in a single oscillator: the Charge horn stack
// is [fundamental, 2nd, 3rd, 4th] = [1, 0.30, 0.15, 0.08]; the submit bump is its
// 110 Hz fundamental + 220 Hz (its 2nd harmonic) = [1, 0.55].
function partialWave(amps) {
  const n = amps.length + 1;
  const real = new Float32Array(n);
  const imag = new Float32Array(n);
  amps.forEach((a, i) => {
    imag[i + 1] = a;
  });
  return ctx.createPeriodicWave(real, imag, { disableNormalization: true });
}

const HORN = [1, 0.3, 0.15, 0.08];
const BUMP = [1, 0.55];

// Render an iOS envelope shape as a gain curve for setValueCurveAtTime. `gain`
// folds in the iOS per-sound masterVolume (~0.50). Faithful to the SoundService
// formulas: 'bump' = quadratic attack + power decay; 'staccato' = smoothstep
// attack + power decay; 'held' = smoothstep attack, full sustain, then a power
// release over the tail.
function envelopeCurve(kind, durSec, opts = {}) {
  const N = Math.min(2048, Math.max(16, Math.round(durSec * 600)));
  const out = new Float32Array(N);
  const attack = opts.attack ?? 0.012;
  const gain = opts.gain ?? 1;
  for (let i = 0; i < N; i += 1) {
    const t = (i / (N - 1)) * durSec;
    let env;
    if (t < attack) {
      const c = t / attack;
      env = kind === 'bump' ? c * c : c * c * (3 - 2 * c);
    } else if (kind === 'held') {
      const releaseStart = durSec * (opts.releaseFraction ?? 0.6);
      if (t < releaseStart) {
        env = 1;
      } else {
        const r = (t - releaseStart) / (durSec - releaseStart);
        env = Math.pow(Math.max(0, 1 - r), opts.releasePower ?? 1.8);
      }
    } else {
      const dp = (t - attack) / (durSec - attack);
      env = Math.pow(Math.max(0, 1 - dp), opts.decayPower ?? 2.0);
    }
    out[i] = Math.max(0, env) * gain;
  }
  return out;
}

// Schedule one note: an oscillator (voiced via a PeriodicWave, square, or plain
// sine) gated by its own gain envelope, routed to `dest` (defaults to the master
// bus). Held notes get a ~5.5 Hz / ±5 % vibrato starting 0.20 s in (mirrors the
// iOS held-note warble that rings out the resolution).
function scheduleNote(startT, { freq, dur, wave, env, held, dest = master }) {
  const osc = ctx.createOscillator();
  if (wave === 'square' || wave === 'sine') osc.type = wave;
  else osc.setPeriodicWave(partialWave(wave));
  osc.frequency.value = freq;

  const g = ctx.createGain();
  g.gain.value = 0;
  g.gain.setValueCurveAtTime(env, startT, dur);
  osc.connect(g);
  g.connect(dest);
  osc.start(startT);
  osc.stop(startT + dur + 0.03);

  if (held) {
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 5.5;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = freq * 0.05;
    lfo.connect(lfoGain).connect(osc.frequency);
    lfo.start(startT + 0.2);
    lfo.stop(startT + dur + 0.03);
  }
}

// Schedule one GLIDING note: same envelope/routing as scheduleNote, but the
// oscillator's frequency ramps linearly from `fromFreq` to `toFreq` over
// `glideDur` seconds then holds — the portamento used by the seal's rising
// flare (iOS: a phase-accumulated glide; Web Audio's native frequency ramp
// produces the same linear-in-time sweep).
function scheduleGlideNote(startT, { fromFreq, toFreq, glideDur, dur, wave, env, dest = master }) {
  const osc = ctx.createOscillator();
  osc.setPeriodicWave(partialWave(wave));
  osc.frequency.setValueAtTime(fromFreq, startT);
  osc.frequency.linearRampToValueAtTime(toFreq, startT + glideDur);

  const g = ctx.createGain();
  g.gain.value = 0;
  g.gain.setValueCurveAtTime(env, startT, dur);
  osc.connect(g);
  g.connect(dest);
  osc.start(startT);
  osc.stop(startT + dur + 0.03);
}

// The pick-note ladder — one note per rack-ball fill during the Results reveal
// (and the iOS ballot's per-answer notes): the Charge! call broken into its
// per-tap notes, G4–C5–E5–G5–A5–G5. Notes 1–5 staccato (~250 ms); note 6 held
// (~480 ms) with the vibrato ring-out. Port of SoundService.playPickNote —
// same horn voice as the submit fanfare so pick→submit→reveal share one
// musical identity (Epic 1, 2026-07-02).
// Notes 7–8 extend the run for the G2 actual-count reveal rack (a multi-day
// night can climb past six) — the ladder keeps ascending to its top instead
// of replaying note 6. The 9th+ correct plays no note (iOS breaks into the
// jackpot cascade there; web lets the visual run carry it).
const PICK_LADDER = [HZ.G4, HZ.C5, HZ.E5, HZ.G5, HZ.A5, HZ.G5, HZ.B5, HZ.C6];
let lastPickNoteAt = 0;

export function playPickNote(order = 1) {
  if (shouldSkip() || !ctx) return;
  try {
    // 100 ms debounce, mirroring iOS — rapid fills never replay a note.
    const now = Date.now();
    if (now - lastPickNoteAt < 100) return;
    lastPickNoteAt = now;

    const i = Math.max(1, Math.min(8, order)) - 1;
    const held = i === 5;
    const dur = held ? 0.48 : 0.25;
    const start = ctx.currentTime + 0.01;
    const env = envelopeCurve(held ? 'held' : 'staccato', dur, {
      attack: held ? 0.025 : 0.018,
      gain: 0.5,
      releaseFraction: 0.5,
      releasePower: 1.8,
      decayPower: 2.0,
    });
    scheduleNote(start, { freq: PICK_LADDER[i], dur, wave: HORN, env, held });
  } catch {
    /* sound failures never surface into the reveal flow */
  }
}

// The submit BUMP — fires on the submit-button tap. 110 Hz + 220 Hz over ~240 ms,
// quadratic attack + steep (pow 3.6) decay. unlockAudio() must have run first.
export function playSubmitBump() {
  if (shouldSkip() || !ctx) return;
  try {
    const start = ctx.currentTime + 0.01;
    const env = envelopeCurve('bump', 0.24, { attack: 0.008, decayPower: 3.6, gain: 0.5 });
    scheduleNote(start, { freq: 110, dur: 0.24, wave: BUMP, env });
  } catch {
    /* never let a sound failure surface into the submit flow */
  }
}

// The seal chord — the flame-ignite arpeggio plus a C4 root for body (mirrors
// iOS makeSubmitSealBuffer's `chord` table exactly: freq, relative amp, start
// offset in seconds). Each voice sounds through SEAL_CHORD_WAVE (fundamental +
// 0.50×2nd + 0.22×3rd harmonic — the additive stack iOS sums per chord note).
const SEAL_CHORD = [
  { freq: HZ.C4, amp: 0.5, start: 0.0 },
  { freq: HZ.C5, amp: 1.0, start: 0.0 },
  { freq: HZ.E5, amp: 0.88, start: 0.05 },
  { freq: HZ.G5, amp: 0.88, start: 0.1 },
  { freq: HZ.C6, amp: 0.75, start: 0.15 },
];
// Bonus-day extension — the same arpeggio one octave up, entering after the
// base chord has landed and sharing its long ring.
const SEAL_CHORD_BONUS_EXTRA = [
  { freq: HZ.E6, amp: 0.55, start: 0.42 },
  { freq: HZ.G6, amp: 0.55, start: 0.5 },
  { freq: HZ.C7, amp: 0.48, start: 0.58 },
];
const SEAL_DURATION = 0.95;
const SEAL_BONUS_DURATION = 1.35;
const SEAL_CHORD_ATTACK = 0.008;
const SEAL_CHORD_DECAY_POWER = 1.6; // rings slightly longer than the flame-ignite profile pulse
const SEAL_CHORD_VOLUME = 0.16; // matches iOS chordVolume — keeps the 5-voice stack from summing hot
const SEAL_CHORD_WAVE = [1, 0.5, 0.22];
const SEAL_FLARE_WAVE = [1, 0.35]; // the flare's harmonic stack (fundamental + 0.35×2nd)

// The submit FANFARE — fires when the celebration's streak number lands. The
// seal: a rising flare (G3→C5) into the staggered C-major chord with a C7
// shimmer, exactly as iOS playSubmitSeal(). Bonus days (streak a positive
// multiple of 6) get playSubmitSealBonus()'s octave-up extension instead —
// same gesture, one more step.
export function playSubmitFanfare(streak = 0) {
  if (shouldSkip() || !ctx) return;
  try {
    const bonus = streak > 0 && streak % 6 === 0;
    const totalDuration = bonus ? SEAL_BONUS_DURATION : SEAL_DURATION;
    const chord = bonus ? [...SEAL_CHORD, ...SEAL_CHORD_BONUS_EXTRA] : SEAL_CHORD;
    const start = ctx.currentTime + 0.02;

    // The seal's five-to-eight voices ring simultaneously (unlike the old
    // Charge melody's mostly-sequential notes) — route them through a
    // dedicated compressor bus as a summing-spike safety net. iOS instead
    // hard-clamps each raw sample; Web Audio has no destination clamp.
    const sealBus = ctx.createDynamicsCompressor();
    sealBus.connect(master);

    for (const note of chord) {
      const noteDur = totalDuration - note.start;
      const env = envelopeCurve('staccato', noteDur, {
        attack: SEAL_CHORD_ATTACK,
        decayPower: SEAL_CHORD_DECAY_POWER,
        gain: note.amp * SEAL_CHORD_VOLUME,
      });
      scheduleNote(start + note.start, {
        freq: note.freq,
        dur: noteDur,
        wave: SEAL_CHORD_WAVE,
        env,
        dest: sealBus,
      });
    }

    // Rising flare — glides G3 (196 Hz) up into the C5 chord root.
    const flareDur = 0.34;
    const flareEnv = envelopeCurve('staccato', flareDur, { attack: 0.01, decayPower: 2.2, gain: 0.13 });
    scheduleGlideNote(start, {
      fromFreq: 196.0,
      toFreq: HZ.C5,
      glideDur: 0.16,
      dur: flareDur,
      wave: SEAL_FLARE_WAVE,
      env: flareEnv,
      dest: sealBus,
    });

    // Bright C7 shimmer for sheen.
    const shimmerDur = 0.3;
    const shimmerEnv = envelopeCurve('staccato', shimmerDur, { attack: 0.004, decayPower: 3.0, gain: 0.08 });
    scheduleNote(start + 0.12, { freq: HZ.C7, dur: shimmerDur, wave: 'sine', env: shimmerEnv, dest: sealBus });
  } catch {
    /* never let a sound failure surface into the submit flow */
  }
}
